import { useEffect, useState } from "react";
import api, { eur, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BLANK = { name: "", pay_rate: "", overtime_rate: "", apply_to_existing: false };

export function JobRolesManager({ cfg = {} }) {
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = () => api.get("/job-roles", cfg).then((r) => setRoles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEdit(null); setForm(BLANK); setOpen(true); };
  const openEdit = (r) => { setEdit(r); setForm({ name: r.name, pay_rate: r.pay_rate, overtime_rate: r.overtime_rate, apply_to_existing: false }); setOpen(true); };
  const onPay = (v) => setForm((f) => ({ ...f, pay_rate: v, overtime_rate: (!edit && v) ? +(Number(v) * 1.5).toFixed(2) : f.overtime_rate }));
  const save = async () => {
    if (!form.name.trim()) { toast.error("Role name is required"); return; }
    const body = { name: form.name.trim(), pay_rate: Number(form.pay_rate || 0), overtime_rate: Number(form.overtime_rate || 0) };
    try {
      if (edit) {
        body.apply_to_existing = !!form.apply_to_existing;
        const r = await api.put(`/job-roles/${edit.id}`, body, cfg);
        toast.success(r.data?.updated_staff ? `Role updated · ${r.data.updated_staff} staff re-rated` : "Role updated");
      } else { await api.post("/job-roles", body, cfg); toast.success("Role added"); }
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"? Existing staff keep their current rate.`)) return;
    try { await api.delete(`/job-roles/${r.id}`, cfg); toast.success("Role deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const rateChanged = edit && (Number(form.pay_rate) !== Number(edit.pay_rate) || Number(form.overtime_rate) !== Number(edit.overtime_rate));

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between gap-4 mb-3">
        <p className="text-sm text-zinc-500">Your company's job titles and default rates. These drive the job-title dropdown and default pay on the employee form.</p>
        <Button data-testid="job-role-add-btn" onClick={openNew} className="bg-slate-900 text-white whitespace-nowrap">+ Add role</Button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3">Job role</th><th className="p-3 text-right">Default rate</th><th className="p-3 text-right">Overtime</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {roles.length === 0 && <tr><td colSpan={4} data-testid="job-roles-empty" className="p-6 text-center text-zinc-400">No roles yet.</td></tr>}
            {roles.map((r) => (
              <tr key={r.id} data-testid={`job-role-row-${r.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                <td className="p-3 font-medium text-slate-900">{r.name}</td>
                <td className="p-3 num text-right">{eur(r.pay_rate)}<span className="text-zinc-400 text-xs">/hr</span></td>
                <td className="p-3 num text-right text-zinc-500">{eur(r.overtime_rate)}<span className="text-zinc-400 text-xs">/hr</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button data-testid={`job-role-edit-${r.id}`} onClick={() => openEdit(r)} className="text-zinc-400 hover:text-slate-900 mr-3"><Pencil className="h-4 w-4 inline" /></button>
                  <button data-testid={`job-role-del-${r.id}`} onClick={() => del(r)} className="text-zinc-300 hover:text-rose-500"><Trash2 className="h-4 w-4 inline" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{edit ? "Edit role" : "Add role"}</DialogTitle>
            <DialogDescription>Set the job title and its default hourly and overtime rates.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Role name</Label><Input data-testid="jr-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="e.g. 3rd Year Apprentice" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Default rate €/hr</Label><Input data-testid="jr-pay" type="number" step="0.25" value={form.pay_rate} onChange={(e) => onPay(e.target.value)} className="mt-1" /></div>
              <div><Label>Overtime €/hr</Label><Input data-testid="jr-ot" type="number" step="0.25" value={form.overtime_rate} onChange={(e) => setForm({ ...form, overtime_rate: e.target.value })} className="mt-1" /></div>
            </div>
            {rateChanged && (
              <label className="flex items-start gap-2 text-sm rounded-md border border-amber-200 bg-amber-50 p-3 cursor-pointer">
                <input data-testid="jr-apply" type="checkbox" checked={form.apply_to_existing} onChange={(e) => setForm({ ...form, apply_to_existing: e.target.checked })} className="mt-0.5" />
                <span className="text-amber-800">Also update existing staff on this role to the new rate. <span className="text-amber-600">Leave unticked to apply to new hires only.</span></span>
              </label>
            )}
          </div>
          <Button data-testid="jr-save" onClick={save} className="w-full bg-slate-900 text-white mt-2">{edit ? "Save changes" : "Add role"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
